import {
  Insertion,
  JsonPatch,
  LayoutModel,
  ModelCategory,
  Project,
  ProjectChange,
  ProjectLayoutModel,
  ProjectSynchronizationPayload,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { assertExhaustiveSwitch } from '@paragon/utilities/lib/utilities';

import { migrateIncomingProject } from '../loadedData';
import {
  getInsertionsWithoutPendingChangesGroupedByObjectGuid,
  getInsertionsWithPendingChangesGroupedByObjectGuid,
  getLayoutModelsWithPendingChanges,
} from '../selectors/changeTracker';
import { getProjectAncestorFromLayoutModelsAndInsertions } from '../selectors/getProjectAncestor';
import { LayoutModelsLibraryState, LayoutModelsLibraryThunkAction } from '../store';
import { projectSynchronized } from '../store/layoutModelsSlice';
import { buildProjectSynchronizationPayload, fetchComponentDesignData } from './syncActions';

export const saveAllProjectChanges =
  (): LayoutModelsLibraryThunkAction<Promise<void>> => async (dispatch, getState) => {
    const changes = new ProjectChangeFactory(getState());

    const syncPayloads: Record<string, ProjectSynchronizationPayload> = {};

    for (const project of changes.getAffectedProjects()) {
      syncPayloads[project.projectGuid] = await saveProjectChanges(getState(), project);
    }

    dispatch(projectSynchronized(syncPayloads));
  };

export const saveChangesForSingleProject =
  (projectGuid: string): LayoutModelsLibraryThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const changes = new ProjectChangeFactory(getState()).getForProject(projectGuid);
    if (!changes) {
      return;
    }

    const syncPayload = await saveProjectChanges(getState(), changes);
    dispatch(projectSynchronized({ [changes.projectGuid]: syncPayload }));
  };

export async function saveProjectChanges(state: LayoutModelsLibraryState, changes: ProjectChange) {
  const createdProjectLayoutModel = changes.created;
  if (createdProjectLayoutModel) {
    const newProject: Project = {
      ...createdProjectLayoutModel,
      insertions: [],
      layoutModels: {},
      trussGroups: {},
    };
    await designService.projects.create(newProject);
  }

  const updatedProject = await designService.projects.applyChanges(changes);
  const projectData = migrateIncomingProject(updatedProject);
  return buildProjectSynchronizationPayload(state, projectData, updatedProject.guid, {
    componentDesignData: await fetchComponentDesignData(Array.from(projectData.componentDesigns)),
    changes,
  });
}

function createProjectChange(projectGuid: string): ProjectChange {
  return {
    projectGuid,
    patches: [],
    createdModels: [],
    updatedModels: {},
    createdInsertions: [],
    removedInsertions: [],
  };
}

export class ProjectChangeFactory {
  private readonly state: LayoutModelsLibraryState;
  private readonly layoutModels: Record<string, LayoutModel>;

  private projectChanges = new Map<string, ProjectChange>();

  constructor(state: LayoutModelsLibraryState) {
    this.state = state;
    this.layoutModels = getLayoutModelsWithPendingChanges(state);

    const { modifiedModels, modifiedInsertions } = state.changeTracker;

    for (const [modelGuid, changes] of Object.entries(modifiedModels)) {
      switch (changes.type) {
        case 'created':
          this.createModel(changes.model);
          break;
        case 'edited':
          for (const patch of changes.patches) {
            this.patchModel(modelGuid, patch);
          }
          break;
        default:
          assertExhaustiveSwitch(changes);
      }
    }

    for (const createdInsertion of modifiedInsertions.created) {
      this.createInsertion(createdInsertion);
    }

    for (const removedInsertion of modifiedInsertions.removed) {
      this.removeInsertion(removedInsertion);
    }
  }

  getForProject(projectGuid: string) {
    return this.projectChanges.get(projectGuid);
  }

  getAffectedProjects() {
    return this.projectChanges.values();
  }

  private createModel(layoutModel: LayoutModel) {
    const changes = this.getChangesForProjectOfModel(layoutModel.guid);
    if (this.isProject(layoutModel.guid)) {
      changes.created = layoutModel as ProjectLayoutModel;
    } else {
      changes.createdModels.push(layoutModel);
    }
  }

  private patchModel(guid: string, patch: JsonPatch) {
    const changes = this.getChangesForProjectOfModel(guid);

    if (this.isProject(guid)) {
      changes.patches.push(patch);
    } else {
      if (!(guid in changes.updatedModels)) {
        changes.updatedModels[guid] = [];
      }

      changes.updatedModels[guid].push(patch);
    }
  }

  private createInsertion(insertion: Insertion) {
    this.getChangesForProjectOfModel(insertion.parentGuid).createdInsertions.push(insertion);
  }

  private removeInsertion(insertion: Insertion) {
    this.getChangesForProjectOfModel(insertion.parentGuid).removedInsertions.push(insertion);
  }

  private getLayoutModel(guid: string): LayoutModel {
    const layoutModel = this.layoutModels[guid];
    if (!layoutModel) {
      throw new Error(`Could not find created layout model '${guid}'`);
    }

    return layoutModel;
  }

  private getProject(modelGuid: string): ProjectLayoutModel {
    const model = this.layoutModels[modelGuid];
    if (model?.type === 'project') return model as ProjectLayoutModel;

    // Use insertions with pending changes if the model has any insertions present. This should work for the majority of
    //   cases, even during movement between projects, since that is expressed by two discrete "decrease quantity" and
    //   "increase quantity" changes in different projects.
    const insertionsWithPendingChanges = getInsertionsWithPendingChangesGroupedByObjectGuid(this.state);
    if (insertionsWithPendingChanges[modelGuid]) {
      return getProjectAncestorFromLayoutModelsAndInsertions(
        this.layoutModels,
        insertionsWithPendingChanges,
        modelGuid,
      );
    }

    // Failing that, attempt again using insertions without pending changes applied. This is needed in cases where both
    //   the target model and its parent have been un-inserted and both changes are being saved at the same time.
    const insertionsWithoutPendingChanges = getInsertionsWithoutPendingChangesGroupedByObjectGuid(this.state);
    if (insertionsWithoutPendingChanges[modelGuid]) {
      return getProjectAncestorFromLayoutModelsAndInsertions(
        this.layoutModels,
        insertionsWithoutPendingChanges,
        modelGuid,
      );
    }

    throw new Error(`Could not find project for model ${modelGuid}.`);
  }

  private isProject(guid: string): boolean {
    return this.layoutModels[guid]?.category === ModelCategory.Project;
  }

  private getChangesForProjectOfModel(modelGuid: string) {
    return this.getChangesForProject(this.getProject(modelGuid).guid);
  }

  private getChangesForProject(projectGuid: string): ProjectChange {
    if (!this.projectChanges.has(projectGuid)) {
      this.projectChanges.set(projectGuid, createProjectChange(projectGuid));
    }

    return this.projectChanges.get(projectGuid)!;
  }
}
